<template>
  <div>
    <BasePanel
      v-show="(isLoaded || hasErrors) && !isSuccess"
      :title="panelTitle"
      :subheading="panelSubheading"
      :subheading-list="panelSubheadingList"
      subheading-alignment="left"
      class="sentry-mask"
    >
      <template #errors>
        <BaseDisplayMessage v-if="getErrorMessage" class="mt-3 text-danger" :message="getErrorMessage" role="alert" />
      </template>

      <ValidationObserver ref="form" v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(onSubmit)">
          <template v-if="isNasnCohort">
            <h2 class="nasn-section-title">{{ $t('completeActivation.nasn.finishTheDetails') }}</h2>
            <hr class="nasn-divider" />
          </template>

          <DpBaseInput
            id="email-input"
            v-model.trim="email"
            name="email"
            type="email"
            :autofocus="true"
            autocomplete="username"
            :label="'input.email.label' | translate"
            :validation-rules="getEmailRules"
            :placeholder="'input.email.placeholder' | translate"
            vid="email"
          />

          <DpBaseInput
            id="password-input"
            v-model.trim="password"
            name="password"
            type="password"
            use-password-validation-rules
            autocomplete="new-password"
            additional-description="password-check-list"
            :bails="false"
            :validation-state="!hasErrors"
            :label="'input.createPassword.label' | translate"
            :placeholder="'input.createPassword.placeholder' | translate"
          >
            <template #default="{ failedRules, dirty, valid }">
              <dp-secure-password-check-list id="password-check-list" :failed-rules="failedRules" :dirty="dirty" :valid="valid" />
            </template>
          </DpBaseInput>

          <div class="optin-groups" :class="{ 'nasn-optin-groups': isNasnCohort }">
            <!-- Help Get Lost Pets Home: pet alerts + shelter network (cohort shows a section heading) -->
            <div class="nasn-group nasn-group--lost-pets">
              <template v-if="isNasnCohort">
                <h2 class="nasn-section-title">{{ $t('completeActivation.nasn.helpGetLostPetsHome') }}</h2>
              </template>
              <b-form-checkbox v-if="getPetAlertsOptInCopy" v-model="petAlerts" class="my-3">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="getPetAlertsOptInCopy"></span>
              </b-form-checkbox>

              <b-form-checkbox v-if="getShelterNetworkOptInCopy" v-model="shelterNetwork" class="my-3">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="getShelterNetworkOptInCopy"></span>
              </b-form-checkbox>
            </div>

            <!-- Stay Informed: marketing opt-in (cohort shows a section heading) -->
            <div class="nasn-group nasn-group--stay-informed">
              <template v-if="isNasnCohort">
                <h2 class="nasn-section-title">{{ $t('completeActivation.nasn.stayInformed') }}</h2>
              </template>
              <b-form-checkbox v-if="getMarketingCommunicationOptInCopy" v-model="marketingOptIn" class="my-3">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="getMarketingCommunicationOptInCopy"></span>
              </b-form-checkbox>
            </div>

            <!-- Terms of Service + Privacy Policy (cohort renders this last) -->
            <b-row class="nasn-group nasn-group--terms">
              <b-col class="pr-0">
                <ValidationProvider
                  v-slot="{ validated, valid, errors }"
                  :rules="{ required: { allowFalse: false } }"
                  name="terms-of-service-and-privacy-policy"
                  vid="terms-of-service-and-privacy-policy"
                >
                  <b-form-group :label-for="'terms-of-service-and-privacy-policy'">
                    <b-form-checkbox
                      v-if="getTermsOfServiceAndPrivacyPolicyOptInCopy"
                      id="terms-of-service-and-privacy-policy"
                      v-model="eula"
                      class="my-3"
                      :state="(!validated || valid) && null"
                      required
                      v-testid="'signup-tos-input'"
                    >
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <span v-html="getTermsOfServiceAndPrivacyPolicyOptInCopy"></span>
                    </b-form-checkbox>
                    <b-form-invalid-feedback
                      :id="'terms-of-service-and-privacy-policy-invalid-feedback'"
                      :state="(!validated || valid) && null"
                  >
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </ValidationProvider>
            </b-col>
            <b-col cols="1" class="pl-0">
              <b-button
                  ref="tooltipButton"
                  v-if="getTermsOfServiceAndPrivacyPolicyOptInCopyTooltip"
                  v-b-tooltip="{
                    title: getTermsOfServiceAndPrivacyPolicyOptInCopyTooltip,
                    placement: 'top',
                    trigger: 'hover click blur',
                    html: true,
                  }"
                  class="mt-3 tooltip-btn"
                  :title="`${$t('dp.footer.tos')} and ${$t('dp.footer.privacy')} info`"
                  @keydown.esc="handleTooltipEscKey"
                >
                  <fa-icon :icon="['fas', 'info-circle']" class="tooltip-info" />
                </b-button>
              </b-col>
            </b-row>
          </div>

          <b-button type="submit" variant="primary" class="w-100" :disabled="isLoading" v-testid="'signup-submit-action'">
            {{ submitButtonText }}
            <div v-if="isLoading" role="status" class="fa-pulse float-right mt-1">
              <font-awesome-icon :icon="['fas', 'spinner']" class="d-flex" />
              <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
            </div>
          </b-button>
        </form>
      </ValidationObserver>
    </BasePanel>
    <BasePanel
      v-show="!isLoaded && !hasErrors"
      :title="$t('completeActivation.loading.title')"
      :subheading="$t('completeActivation.loading.subheading')"
    >
      <BaseLoadingIcon />
    </BasePanel>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { DpBaseInput, DpSecurePasswordCheckList } from '@docupet/component-library';

export default {
  name: 'CompleteSignup',
  components: {
    DpSecurePasswordCheckList,
    DpBaseInput,
  },
  beforeRouteEnter(to, from, next) {
    const { getters, commit } = require('@/store').default;

    if (to.query?.token) {
      commit('SET_TOKEN', to.query?.token, { root: true });
    }

    if (to.query?.redirect_path) {
      commit('completeSignup/SET_REDIRECT', to.query.redirect_path);
    }

    if (to.query?.redirect_uri) {
      commit('completeSignup/SET_REDIRECT', to.query.redirect_uri);
      let utmParams = {};
      for (let property in to.query) {
        if (property.toLowerCase().startsWith('utm_')) {
          utmParams[property] = to.query[property];
        }
      }
      if (Object.keys(utmParams).length) {
        commit('completeSignup/SET_REDIRECT_QUERY_PARAMS', utmParams);
      }
    }

    const token = getters['getToken'];

    token
      ? next()
      : to.query?.accessCode
      ? next({ name: 'FetchMyRecordByAccessCode', query: { accessCode: to.query?.accessCode } })
      : next({ name: 'FetchMyRecordByAccessCode' });
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    email: '',
    eula: null,
    marketingOptIn: null,
    petAlerts: null,
    shelterNetwork: null,
    password: '',
    emailExistsData: {
      exists: false,
      email: '',
      message: '',
    },
  }),
  computed: {
    ...mapGetters(['getBaseUrl', 'isNasnCohort', 'getPostSignupRedirect']),
    ...mapGetters('city', ['noLicensing']),
    ...mapGetters('completeSignup', [
      'isLoading',
      'isLoaded',
      'isSuccess',
      'hasErrors',
      '_attemptCount',
      'getErrors',
      'getPOFirstName',
      'getPOEmailAddress',
    ]),
    isNonLicensing() {
      return this.noLicensing === true;
    },
    getEmailRules() {
      let ruleObject = { required: true, email: true };
      if (this.emailExistsData.exists) {
        ruleObject.emailExists = [this.emailExistsData.email, this.emailExistsData.message];
      }
      return ruleObject;
    },
    contentsBlank: (self = this) => self.email === '' || self.password === '' || self.eula !== true,
    getErrorMessage: function () {
      if ((this.hasErrors || (this._attemptCount > 1 && !this.isLoading)) && this._attemptCount <= 3) {
        switch (this.getErrors) {
          case 'duplicate_email':
          case 'access_code_used':
            return {
              path: 'completeActivation.errorMessages.access_code_used',
              action: {
                to: this.email ? { name: 'LogIn', query: { email: this.email } } : { name: 'LogIn' },
                text: this.$t('completeActivation.errorMessages.action.access_code_used'),
              },
            };
          default:
            return this.$t('completeActivation.errorMessages.default');
        }
      }
      if ((this.hasErrors || this._attemptCount > 3) && this._attemptCount > 3) {
        return this.$t('completeActivation.errorMessages.contactCustomerCare');
      }
      return null;
    },
    getScreenContext() {
      return this.context;
    },
    petNames() {
      return this.getScreenContext?.petNames || '';
    },
    petCount() {
      return this.getScreenContext?.petCount || 0;
    },
    panelTitle() {
      if (this.isNasnCohort && this.petNames) {
        return this.$t('completeActivation.nasn.title', { petNames: this.petNames });
      }
      if (this.isNonLicensing) {
        return this.$t('completeActivation.nonLicensing.title');
      }
      return this.$t('completeActivation.title', { firstName: this.getPOFirstName || '' });
    },
    panelSubheading() {
      if (this.isNasnCohort && this.petNames) {
        return this.petCount > 1
          ? this.$t('completeActivation.nasn.subheadingPlural')
          : this.$t('completeActivation.nasn.subheadingSingular', { petNames: this.petNames });
      }
      if (this.isNonLicensing) {
        return this.$t('completeActivation.nonLicensing.subheading', {
          petName: this.petNames || this.$t('completeActivation.nonLicensing.petFallback'),
        });
      }
      return this.$t('completeActivation.subheading');
    },
    panelSubheadingList() {
      return this.isNasnCohort || this.isNonLicensing ? null : this.$t('completeActivation.subheadingList');
    },
    submitButtonText() {
      return this.isNasnCohort ? this.$t('completeActivation.nasn.buttonText') : this.$t('completeActivation.buttonText');
    },
    getTermsOfServiceAndPrivacyPolicyOptInCopy() {
      return this.getScreenContext?.termsOfServiceAndPrivacyPolicyOptInCopy;
    },
    getTermsOfServiceAndPrivacyPolicyOptInCopyTooltip() {
      return this.getScreenContext?.termsOfServiceAndPrivacyPolicyOptInCopyTooltip;
    },
    getMarketingCommunicationOptInCopy() {
      return this.getScreenContext?.marketingCommunicationOptInCopy;
    },
    getPetAlertsOptInCopy() {
      return this.getScreenContext?.petAlertsOptInCopy || this.$t('checkbox.petalert');
    },
    getShelterNetworkOptInCopy() {
      return this.getScreenContext?.shelterNetworkOptInCopy || this.$t('checkbox.shelter');
    },
  },
  created() {
    this.fetchPetOwner()
      .then(({ online }) => {
        this.email = this.getPOEmailAddress;
        if (online) this.$router.push({ name: 'SyfLoginSuccessRedirect', query: this.$router.query });
      })
      .catch(() => {
        this.accessCode
          ? this.$router.push({ name: 'FetchMyRecordByAccessCode', query: { accessCode: this.accessCode } })
          : this.$router.push({ name: 'FetchMyRecordByAccessCode' });
      });
  },
  methods: {
    ...mapActions('completeSignup', ['fetchPetOwner', 'completeSignup', 'error']),
    handleTooltipEscKey() {
      this.$root.$emit('bv::hide::tooltip');
    },
    async onSubmit({ email, password, eula, marketingOptIn, petAlerts, shelterNetwork } = this) {
      const { commit } = require('@/store').default;

      marketingOptIn = marketingOptIn ?? false;
      petAlerts = petAlerts ?? false;
      shelterNetwork = shelterNetwork ?? false;

      this.completeSignup({ email, password, eula, marketingOptIn, petAlerts, shelterNetwork })
        .then(({ token }) => {
          // Server-authoritative destination (session-bound cohort); falls back to the Membership Offer.
          commit('completeSignup/SET_REDIRECT', this.getPostSignupRedirect || 'offer/membership');
          this.$router.push({
            name: 'SyfLoginSuccessRedirect',
            query: { ...this.$router.currentRoute.query, token, redirectPath: 'licensing' },
          });
        })
        .catch(() => {
          if (this.getErrors === 'duplicate_email') {
            this.emailExistsData.exists = true;
            this.emailExistsData.email = this.email;
            this.emailExistsData.message = this.$t('dp.input.email.asyncErrorMessage.emailAlreadyExists', { loginLink: this.loginLink() });
          }
          this.errorMessage = this.getErrorMessage;
        });
    },
    loginLink: function () {
      return `${this.getBaseUrl}/login`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';

// The opt-in checkboxes render in DOM order [lost-pets, stay-informed, terms].
// Use flex ordering so the DEFAULT (non-cohort) layout keeps the original visual
// order (Terms, then marketing, then pet-alerts), while the NASN cohort follows
// the Figma order (Help Get Lost Pets Home -> Stay Informed -> Terms).
.optin-groups {
  display: flex;
  flex-direction: column;

  // Default (non-cohort) order: Terms first, marketing, then lost-pet alerts.
  .nasn-group--terms {
    order: 1;
  }
  .nasn-group--stay-informed {
    order: 2;
  }
  .nasn-group--lost-pets {
    order: 3;
  }
}

// NASN cohort order per Figma.
.optin-groups.nasn-optin-groups {
  .nasn-group--lost-pets {
    order: 1;
  }
  .nasn-group--stay-informed {
    order: 2;
  }
  .nasn-group--terms {
    order: 3;
  }
}

.nasn-section-title {
  font-size: 1rem;
  font-weight: 700;
  color: $dp-navy;
  margin: 0 0 0.5rem;
}

.nasn-divider {
  border: 0;
  border-top: 1px solid $dp-gray-3;
  margin: 0.5rem 0 1rem;
}
</style>
